import { createPageQueryDTOSchema } from '@tempo-io/common-dto';
import { WATCHABLE_ENTITY_TYPES } from '@tempo-io/watchable-entity-registry';
import { z } from 'zod';

export const WATCH_STATES = ['watching', 'ignored'] as const;
export const watchStateSchema = z.enum(WATCH_STATES);
export type WatchState = z.infer<typeof watchStateSchema>;

export const WATCH_IGNORE_SCOPES = ['activity', 'all'] as const;
export const watchIgnoreScopeSchema = z.enum(WATCH_IGNORE_SCOPES);
export type WatchIgnoreScope = z.infer<typeof watchIgnoreScopeSchema>;

export const WATCH_SOURCES = ['manual', 'auto'] as const;
export const watchSourceSchema = z.enum(WATCH_SOURCES);
export type WatchSource = z.infer<typeof watchSourceSchema>;

export const watchEntityTypeSchema = z.enum(WATCHABLE_ENTITY_TYPES);

const watchEntityRefShape = {
  entityType: watchEntityTypeSchema,
  entityId: z.string().min(1),
};

export const watchUpsertDTOSchema = z.discriminatedUnion('state', [
  z.object({ ...watchEntityRefShape, state: z.literal('watching') }),
  z.object({
    ...watchEntityRefShape,
    state: z.literal('ignored'),
    ignoreScope: watchIgnoreScopeSchema.default('activity'),
  }),
]);
export type WatchUpsertDTO = z.infer<typeof watchUpsertDTOSchema>;

export const watchDeleteQueryDTOSchema = z.object(watchEntityRefShape);

export const watchSortableFields = ['id'] as const;

export const watchListQueryDTOSchema = createPageQueryDTOSchema({
  sortableFields: watchSortableFields,
  defaultSortBy: 'id',
  defaultSortOrder: 'desc',
})
  .extend({
    state: watchStateSchema.optional(),
    entityType: watchEntityTypeSchema.optional(),
    source: watchSourceSchema.optional(),
    entityId: z.string().min(1).optional(),
  })
  .refine((query) => query.entityId === undefined || query.entityType !== undefined, {
    message: 'entityId requires entityType',
    path: ['entityType'],
  });

export const watchDTOSchema = z.object({
  ...watchEntityRefShape,
  state: watchStateSchema,
  ignoreScope: watchIgnoreScopeSchema,
  source: watchSourceSchema,
  label: z.string().nullable(),
  unavailable: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type WatchDTO = z.infer<typeof watchDTOSchema>;
